import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  talents as talentsApi, orgs as orgsApi, postings as postingsApi,
  applications as applicationsApi, payments as paymentsApi,
  verification as verificationApi, reports as reportsApi,
  announcements as announcementsApi, audit as auditApi, admins as adminsApi,
  categories as categoriesApi, notifications as notificationsApi,
  dashboard as dashboardApi,
} from "../api/endpoints";
import {
  mapTalent, mapOrg, mapPosting, mapApplication, mapPayment,
  mapVerificationItem, mapReport, mapAnnouncement, mapAuditEvent,
  mapAdminUser, mapCategory, mapCategoryOnboarding, mapNotification, mapDashboardStats,
  mapSignups, mapRevenue, EMPTY_STATS,
  type MappedCategory, type MappedNotification, type MappedPlatformStats,
} from "../adapters";
import type {
  AdminTalent, AdminOrg, AdminPosting, AdminApplication, AdminPayment,
  VerificationItem, AdminReport, AdminAnnouncement, AuditEvent, AdminUser,
  CategoryOnboarding,
} from "../data";

const LIST_ALL = { limit: 1000 };

interface AdminDataValue {
  ADMIN_TALENTS: AdminTalent[];
  ADMIN_ORGS: AdminOrg[];
  ADMIN_POSTINGS: AdminPosting[];
  ADMIN_APPLICATIONS: AdminApplication[];
  ADMIN_PAYMENTS: AdminPayment[];
  VERIFICATION_QUEUE: VerificationItem[];
  ADMIN_REPORTS: AdminReport[];
  ADMIN_ANNOUNCEMENTS: AdminAnnouncement[];
  AUDIT_LOG: AuditEvent[];
  ADMIN_USERS: AdminUser[];
  PLATFORM_CATEGORIES: MappedCategory[];
  CATEGORY_ONBOARDING: CategoryOnboarding[];
  ADMIN_NOTIFICATIONS: MappedNotification[];
  PLATFORM_STATS: MappedPlatformStats;
  MONTHLY_SIGNUPS: { month: string; talent: number; orgs: number }[];
  MONTHLY_REVENUE: { month: string; amount: number }[];
  CATEGORY_DISTRIBUTION: { name: string; value: number }[];
  loading: boolean;
  error: string | null;
  refetchTalents: () => void;
  refetchOrgs: () => void;
  refetchPostings: () => void;
  refetchApplications: () => void;
  refetchPayments: () => void;
  refetchVerification: () => void;
  refetchReports: () => void;
  refetchAnnouncements: () => void;
  refetchAudit: () => void;
  refetchAdmins: () => void;
  refetchCategories: () => void;
  refetchNotifications: () => void;
  refetchDashboard: () => void;
}

const AdminDataContext = createContext<AdminDataValue | null>(null);

export function useAdminData(): AdminDataValue {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error("useAdminData must be used within an AdminDataProvider");
  return ctx;
}

export function AdminDataProvider({ children }: { children: ReactNode }) {
  const [talents, setTalents] = useState<AdminTalent[]>([]);
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [postings, setPostings] = useState<AdminPosting[]>([]);
  const [applications, setApplications] = useState<AdminApplication[]>([]);
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [verificationQueue, setVerificationQueue] = useState<VerificationItem[]>([]);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [announcements, setAnnouncements] = useState<AdminAnnouncement[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEvent[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [categories, setCategories] = useState<MappedCategory[]>([]);
  const [categoryOnboarding, setCategoryOnboarding] = useState<CategoryOnboarding[]>([]);
  const [notifications, setNotifications] = useState<MappedNotification[]>([]);
  const [stats, setStats] = useState<MappedPlatformStats>(EMPTY_STATS);
  const [signups, setSignups] = useState<{ month: string; talent: number; orgs: number }[]>([]);
  const [revenue, setRevenue] = useState<{ month: string; amount: number }[]>([]);
  const [categoryDistribution, setCategoryDistribution] = useState<{ name: string; value: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetchOrgs = useCallback(() => {
    orgsApi.list(LIST_ALL).then((r) => setOrgs(r.data.map(mapOrg))).catch((e) => setError(String(e)));
  }, []);

  const refetchTalents = useCallback(() => {
    talentsApi.list(LIST_ALL).then((r) => setTalents(r.data.map(mapTalent))).catch((e) => setError(String(e)));
  }, []);

  const refetchPostings = useCallback(() => {
    Promise.all([postingsApi.list(LIST_ALL), orgsApi.list(LIST_ALL)])
      .then(([p, o]) => {
        const orgNameById = new Map(o.data.map((org) => [org.id, org.name]));
        setPostings(p.data.map((posting) => mapPosting(posting, orgNameById)));
      })
      .catch((e) => setError(String(e)));
  }, []);

  const refetchApplications = useCallback(() => {
    applicationsApi.list(LIST_ALL).then((r) => setApplications(r.data.map(mapApplication))).catch((e) => setError(String(e)));
  }, []);

  const refetchPayments = useCallback(() => {
    paymentsApi.list(LIST_ALL).then((r) => setPayments(r.data.map(mapPayment))).catch((e) => setError(String(e)));
  }, []);

  const refetchVerification = useCallback(() => {
    verificationApi.list(LIST_ALL).then((r) => setVerificationQueue(r.data.map(mapVerificationItem))).catch((e) => setError(String(e)));
  }, []);

  const refetchReports = useCallback(() => {
    reportsApi.list(LIST_ALL).then((r) => setReports(r.data.map(mapReport))).catch((e) => setError(String(e)));
  }, []);

  const refetchAnnouncements = useCallback(() => {
    announcementsApi.list().then((rows) => setAnnouncements(rows.map(mapAnnouncement))).catch((e) => setError(String(e)));
  }, []);

  const refetchAudit = useCallback(() => {
    auditApi.list(LIST_ALL).then((r) => setAuditLog(r.data.map(mapAuditEvent))).catch((e) => setError(String(e)));
  }, []);

  const refetchAdmins = useCallback(() => {
    adminsApi.list().then((rows) => setAdminUsers(rows.map(mapAdminUser))).catch((e) => setError(String(e)));
  }, []);

  const refetchCategories = useCallback(() => {
    categoriesApi.list().then((rows) => {
      setCategories(rows.map(mapCategory));
      // The onboarding questions ride along with the list — this used to be
      // hardcoded to [], so the Category view's question editor was always empty.
      setCategoryOnboarding(rows.map(mapCategoryOnboarding));
    }).catch((e) => setError(String(e)));
  }, []);

  const refetchNotifications = useCallback(() => {
    notificationsApi.list().then((rows) => setNotifications(rows.map(mapNotification))).catch((e) => setError(String(e)));
  }, []);

  const refetchDashboard = useCallback(() => {
    dashboardApi.stats().then((r) => {
      setStats(mapDashboardStats(r.stats));
      setSignups(mapSignups(r.signups));
      setRevenue(mapRevenue(r.revenue));
      setCategoryDistribution(r.categories);
    }).catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      orgsApi.list(LIST_ALL),
      talentsApi.list(LIST_ALL),
      postingsApi.list(LIST_ALL),
      applicationsApi.list(LIST_ALL),
      paymentsApi.list(LIST_ALL),
      verificationApi.list(LIST_ALL),
      reportsApi.list(LIST_ALL),
      announcementsApi.list(),
      auditApi.list(LIST_ALL),
      adminsApi.list(),
      categoriesApi.list(),
      notificationsApi.list(),
      dashboardApi.stats(),
    ]).then(([o, t, p, a, pay, v, r, ann, aud, adm, cat, notif, dash]) => {
      if (cancelled) return;
      const orgNameById = new Map(o.data.map((org) => [org.id, org.name]));
      setOrgs(o.data.map(mapOrg));
      setTalents(t.data.map(mapTalent));
      setPostings(p.data.map((posting) => mapPosting(posting, orgNameById)));
      setApplications(a.data.map(mapApplication));
      setPayments(pay.data.map(mapPayment));
      setVerificationQueue(v.data.map(mapVerificationItem));
      setReports(r.data.map(mapReport));
      setAnnouncements(ann.map(mapAnnouncement));
      setAuditLog(aud.data.map(mapAuditEvent));
      setAdminUsers(adm.map(mapAdminUser));
      setCategories(cat.map(mapCategory));
      setCategoryOnboarding(cat.map(mapCategoryOnboarding));
      setNotifications(notif.map(mapNotification));
      setStats(mapDashboardStats(dash.stats));
      setSignups(mapSignups(dash.signups));
      setRevenue(mapRevenue(dash.revenue));
      setCategoryDistribution(dash.categories);
    }).catch((e) => {
      if (!cancelled) setError(String(e));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo<AdminDataValue>(() => ({
    ADMIN_TALENTS: talents,
    ADMIN_ORGS: orgs,
    ADMIN_POSTINGS: postings,
    ADMIN_APPLICATIONS: applications,
    ADMIN_PAYMENTS: payments,
    VERIFICATION_QUEUE: verificationQueue,
    ADMIN_REPORTS: reports,
    ADMIN_ANNOUNCEMENTS: announcements,
    AUDIT_LOG: auditLog,
    ADMIN_USERS: adminUsers,
    PLATFORM_CATEGORIES: categories,
    CATEGORY_ONBOARDING: categoryOnboarding,
    ADMIN_NOTIFICATIONS: notifications,
    PLATFORM_STATS: stats,
    MONTHLY_SIGNUPS: signups,
    MONTHLY_REVENUE: revenue,
    CATEGORY_DISTRIBUTION: categoryDistribution,
    loading,
    error,
    refetchTalents, refetchOrgs, refetchPostings, refetchApplications, refetchPayments,
    refetchVerification, refetchReports, refetchAnnouncements, refetchAudit, refetchAdmins,
    refetchCategories, refetchNotifications, refetchDashboard,
  }), [
    talents, orgs, postings, applications, payments, verificationQueue, reports,
    announcements, auditLog, adminUsers, categories, categoryOnboarding, notifications, stats, signups,
    revenue, categoryDistribution, loading, error,
    refetchTalents, refetchOrgs, refetchPostings, refetchApplications, refetchPayments,
    refetchVerification, refetchReports, refetchAnnouncements, refetchAudit, refetchAdmins,
    refetchCategories, refetchNotifications, refetchDashboard,
  ]);

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
}
